import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { emitTourEvent } from "@/hooks/use-guided-tour";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Clock, Plus, Edit2, Trash2, Loader2, User as UserIcon } from "lucide-react";
import { useSettingsFormat } from "@/hooks/use-settings-format";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type LaborEntry = {
  id: number;
  projectId: number;
  userId: number;
  startTime: string;
  endTime: string | null;
  durationMinutes: number | null;
  hourlyRate: number | null;
  notes: string | null;
  workerName: string;
  createdAt: string;
};

const LABOR_QUERY_KEY = (projectId: number) => ["field", "projects", projectId, "labor"];
const OPEN_QUERY_KEY = (projectId: number) => ["field", "projects", projectId, "labor", "open"];

function formatDuration(minutes: number | null): string {
  if (minutes == null) return "—";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function formatDatetimeLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function nowLocal(): string {
  return formatDatetimeLocal(new Date().toISOString());
}

export default function FieldLaborTab({ projectId }: { projectId: number }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const queryClient = useQueryClient();
  const isField = user?.role === "field";

  const entriesQuery = useQuery<LaborEntry[]>({
    queryKey: LABOR_QUERY_KEY(projectId),
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/field/projects/${projectId}/labor`);
      if (!res.ok) throw new Error();
      return res.json();
    },
  });

  const openQuery = useQuery<LaborEntry | null>({
    queryKey: OPEN_QUERY_KEY(projectId),
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/field/projects/${projectId}/labor/open`);
      if (!res.ok) throw new Error();
      return res.json();
    },
  });

  const entries = entriesQuery.data ?? [];
  const openEntry = openQuery.data ?? null;

  // --- Clock In dialog ---
  const [clockInOpen, setClockInOpen] = useState(false);
  const [clockInTime, setClockInTime] = useState(nowLocal());
  const [clockInNotes, setClockInNotes] = useState("");

  const clockInMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/field/projects/${projectId}/labor/clock-in`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ startTime: new Date(clockInTime).toISOString(), notes: clockInNotes || null }),
      });
      if (!res.ok) throw new Error();
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Clocked in" });
      queryClient.invalidateQueries({ queryKey: LABOR_QUERY_KEY(projectId) });
      queryClient.invalidateQueries({ queryKey: OPEN_QUERY_KEY(projectId) });
      setClockInOpen(false);
      setClockInNotes("");
      emitTourEvent("field.time-logged");
    },
    onError: () => toast({ title: "Failed to clock in", variant: "destructive" }),
  });

  // --- Clock Out dialog ---
  const [clockOutOpen, setClockOutOpen] = useState(false);
  const [clockOutTime, setClockOutTime] = useState(nowLocal());
  const [clockOutNotes, setClockOutNotes] = useState("");

  const clockOutMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/field/projects/${projectId}/labor/${openEntry!.id}/clock-out`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ endTime: new Date(clockOutTime).toISOString(), notes: clockOutNotes || null }),
      });
      if (!res.ok) throw new Error();
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Clocked out" });
      queryClient.invalidateQueries({ queryKey: LABOR_QUERY_KEY(projectId) });
      queryClient.invalidateQueries({ queryKey: OPEN_QUERY_KEY(projectId) });
      setClockOutOpen(false);
      setClockOutNotes("");
    },
    onError: () => toast({ title: "Failed to clock out", variant: "destructive" }),
  });

  // --- Edit dialog ---
  const [editEntry, setEditEntry] = useState<LaborEntry | null>(null);
  const [editStart, setEditStart] = useState("");
  const [editEnd, setEditEnd] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const openEdit = (entry: LaborEntry) => {
    setEditEntry(entry);
    setEditStart(formatDatetimeLocal(entry.startTime));
    setEditEnd(formatDatetimeLocal(entry.endTime));
    setEditNotes(entry.notes ?? "");
  };

  const editMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/field/labor/${editEntry!.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          startTime: new Date(editStart).toISOString(),
          endTime: editEnd ? new Date(editEnd).toISOString() : null,
          notes: editNotes || null,
        }),
      });
      if (!res.ok) throw new Error();
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Entry updated" });
      queryClient.invalidateQueries({ queryKey: LABOR_QUERY_KEY(projectId) });
      queryClient.invalidateQueries({ queryKey: OPEN_QUERY_KEY(projectId) });
      setEditEntry(null);
    },
    onError: () => toast({ title: "Failed to update entry", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (entryId: number) => {
      const res = await fetch(`${BASE}/api/field/labor/${entryId}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
    },
    onSuccess: () => {
      toast({ title: "Entry deleted" });
      queryClient.invalidateQueries({ queryKey: LABOR_QUERY_KEY(projectId) });
      queryClient.invalidateQueries({ queryKey: OPEN_QUERY_KEY(projectId) });
    },
    onError: () => toast({ title: "Failed to delete entry", variant: "destructive" }),
  });

  // Totals
  const totalMinutes = entries.reduce((sum, e) => sum + (e.durationMinutes ?? 0), 0);
  const totalCost = entries.reduce((sum, e) => {
    if (e.durationMinutes == null || e.hourlyRate == null) return sum;
    return sum + (e.durationMinutes / 60) * e.hourlyRate;
  }, 0);

  return (
    <div>
      {/* Summary + action */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem", flexWrap: "wrap", gap: "0.75rem" }}>
        <div style={{ display: "flex", gap: "1.5rem" }}>
          <div>
            <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Total Hours</div>
            <div style={{ fontSize: "1.5rem", fontWeight: 700 }}>{formatDuration(totalMinutes)}</div>
          </div>
          {totalCost > 0 && (
            <div>
              <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Labor Cost</div>
              <div style={{ fontSize: "1.5rem", fontWeight: 700 }}>
                {formatCurrency(totalCost)}
              </div>
            </div>
          )}
        </div>
        {/* Clock In/Out buttons are hidden for field workers — the persistent action bar
            in project.tsx shows them on both tabs so they're always accessible. */}
        {!isField && (
          <div style={{ display: "flex", gap: "0.5rem" }} data-tour-target="field-log-time-btn">
            {openEntry ? (
              <Button
                onClick={() => { setClockOutTime(nowLocal()); setClockOutOpen(true); }}
                variant="outline"
                style={{ borderColor: "hsl(var(--destructive))", color: "hsl(var(--destructive))" }}
              >
                <Clock style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} />
                Clock Out
              </Button>
            ) : (
              <Button onClick={() => { setClockInTime(nowLocal()); setClockInOpen(true); }}>
                <Clock style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} />
                Clock In
              </Button>
            )}
            <Button variant="outline" onClick={() => { setClockInTime(nowLocal()); setClockInOpen(true); }}>
              <Plus style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} />
              Add Entry
            </Button>
          </div>
        )}
      </div>

      {/* Open entry banner */}
      {openEntry && (
        <div style={{ backgroundColor: "#fef9c3", border: "1px solid #fde047", borderRadius: "0.5rem", padding: "0.75rem 1rem", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.875rem", color: "#713f12" }}>
          <Clock style={{ width: "1rem", height: "1rem", flexShrink: 0 }} />
          <span>
            <strong>{openEntry.workerName}</strong> clocked in at{" "}
            {new Date(openEntry.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} on{" "}
            {new Date(openEntry.startTime).toLocaleDateString()}.
          </span>
        </div>
      )}

      {/* Entries list */}
      {entriesQuery.isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
          <Loader2 style={{ width: "1.5rem", height: "1.5rem" }} className="animate-spin text-muted-foreground" />
        </div>
      ) : entries.length === 0 ? (
        <div style={{ textAlign: "center", padding: "2.5rem", color: "hsl(var(--muted-foreground))" }}>
          <Clock style={{ width: "2.5rem", height: "2.5rem", margin: "0 auto 0.75rem", opacity: 0.3 }} />
          <p style={{ fontWeight: 500 }}>No time entries yet</p>
          <p style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}>Use Clock In to start logging time.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {entries.map((entry) => (
            <div
              key={entry.id}
              style={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "0.5rem", padding: "0.875rem 1rem", display: "flex", alignItems: "flex-start", gap: "0.75rem" }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                  {!isField && (
                    <span style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.8125rem", fontWeight: 600 }}>
                      <UserIcon style={{ width: "0.75rem", height: "0.75rem", color: "hsl(var(--muted-foreground))" }} />
                      {entry.workerName}
                    </span>
                  )}
                  <span style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>
                    {new Date(entry.startTime).toLocaleDateString()}
                  </span>
                  {!entry.endTime && (
                    <span style={{ fontSize: "0.7rem", fontWeight: 600, backgroundColor: "#fef9c3", color: "#713f12", padding: "0.1rem 0.4rem", borderRadius: "9999px" }}>Open</span>
                  )}
                </div>
                <div style={{ fontSize: "0.875rem", marginTop: "0.25rem", color: "hsl(var(--foreground))" }}>
                  {new Date(entry.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  {" → "}
                  {entry.endTime
                    ? new Date(entry.endTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                    : <span style={{ color: "hsl(var(--muted-foreground))" }}>not clocked out</span>}
                  {entry.durationMinutes != null && (
                    <span style={{ marginLeft: "0.5rem", fontWeight: 600, color: "hsl(var(--primary))" }}>
                      {formatDuration(entry.durationMinutes)}
                    </span>
                  )}
                </div>
                {entry.notes && (
                  <div style={{ fontSize: "0.8rem", color: "hsl(var(--muted-foreground))", marginTop: "0.25rem", fontStyle: "italic" }}>{entry.notes}</div>
                )}
              </div>
              <div style={{ display: "flex", gap: "0.375rem", flexShrink: 0 }}>
                <Button variant="ghost" size="icon" style={{ width: "2rem", height: "2rem" }} onClick={() => openEdit(entry)}>
                  <Edit2 style={{ width: "0.875rem", height: "0.875rem" }} />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" style={{ width: "2rem", height: "2rem", color: "hsl(var(--destructive))" }}>
                      <Trash2 style={{ width: "0.875rem", height: "0.875rem" }} />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
                      <AlertDialogDescription>The associated labor cost record will also be removed.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => deleteMutation.mutate(entry.id)}
                        style={{ backgroundColor: "hsl(var(--destructive))", color: "hsl(var(--destructive-foreground))" }}
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Clock In dialog */}
      <Dialog open={clockInOpen} onOpenChange={(o) => { if (!o) setClockInOpen(false); }}>
        <DialogContent style={{ maxWidth: "24rem" }}>
          <DialogHeader><DialogTitle>Clock In</DialogTitle></DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "0.25rem 0" }}>
            <div>
              <Label>Start Time</Label>
              <Input type="datetime-local" value={clockInTime} onChange={(e) => setClockInTime(e.target.value)} style={{ marginTop: "0.375rem" }} />
              <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", marginTop: "0.25rem" }}>Pre-filled with current time — adjust if needed.</p>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea value={clockInNotes} onChange={(e) => setClockInNotes(e.target.value)} placeholder="Any notes for this shift…" style={{ marginTop: "0.375rem", minHeight: "5rem" }} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClockInOpen(false)}>Cancel</Button>
            <Button onClick={() => clockInMutation.mutate()} disabled={clockInMutation.isPending || !clockInTime}>
              {clockInMutation.isPending && <Loader2 style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} className="animate-spin" />}
              Clock In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clock Out dialog */}
      <Dialog open={clockOutOpen} onOpenChange={(o) => { if (!o) setClockOutOpen(false); }}>
        <DialogContent style={{ maxWidth: "24rem" }}>
          <DialogHeader><DialogTitle>Clock Out</DialogTitle></DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "0.25rem 0" }}>
            {openEntry && (
              <div style={{ backgroundColor: "hsl(var(--muted))", borderRadius: "0.5rem", padding: "0.75rem", fontSize: "0.875rem" }}>
                Clocked in at {new Date(openEntry.startTime).toLocaleString()}
              </div>
            )}
            <div>
              <Label>End Time</Label>
              <Input type="datetime-local" value={clockOutTime} onChange={(e) => setClockOutTime(e.target.value)} style={{ marginTop: "0.375rem" }} />
              <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", marginTop: "0.25rem" }}>Pre-filled with current time — adjust if needed.</p>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea value={clockOutNotes} onChange={(e) => setClockOutNotes(e.target.value)} placeholder="Any notes for this shift…" style={{ marginTop: "0.375rem", minHeight: "5rem" }} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClockOutOpen(false)}>Cancel</Button>
            <Button onClick={() => clockOutMutation.mutate()} disabled={clockOutMutation.isPending || !clockOutTime}
              style={{ borderColor: "hsl(var(--destructive))", backgroundColor: "hsl(var(--destructive))", color: "hsl(var(--destructive-foreground))" }}>
              {clockOutMutation.isPending && <Loader2 style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} className="animate-spin" />}
              Clock Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit entry dialog */}
      <Dialog open={!!editEntry} onOpenChange={(o) => { if (!o) setEditEntry(null); }}>
        <DialogContent style={{ maxWidth: "24rem" }}>
          <DialogHeader><DialogTitle>Edit Time Entry</DialogTitle></DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "0.25rem 0" }}>
            <div>
              <Label>Start Time</Label>
              <Input type="datetime-local" value={editStart} onChange={(e) => setEditStart(e.target.value)} style={{ marginTop: "0.375rem" }} />
            </div>
            <div>
              <Label>End Time</Label>
              <Input type="datetime-local" value={editEnd} onChange={(e) => setEditEnd(e.target.value)} style={{ marginTop: "0.375rem" }} />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} style={{ marginTop: "0.375rem", minHeight: "5rem" }} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEntry(null)}>Cancel</Button>
            <Button onClick={() => editMutation.mutate()} disabled={editMutation.isPending}>
              {editMutation.isPending && <Loader2 style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} className="animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
